import { Icon } from "@/icons/icon"
import { cn } from "@/lib/utils"
import { V2InputType, V2ParameterSection } from "@/packaging-v2/lib/schema"
import { isSectionComplete } from "@/packaging-v2/lib/form"
import {
  formatTimeWindowForDisplay,
  getParamErrorFlagState,
} from "@/packaging-v2/lib/utils"
import { isValueWithinLimits } from "@/packaging-v2/lib/validators"
import FrequencyWidget from "@/packaging-v2/components/frequency-widget"
import LeakageWidget from "@/packaging-v2/components/leakage-widget"
import {
  V2ParamFormData,
  V2SectionFormData,
} from "@/store/packaging-v2"
import { FormParamStatus } from "@/lib/form/types"
import Select, { SelectOption } from "@/components/select"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import ToggleGroup from "@/components/toggle-group"
import DatePicker from "@/components/date-picker"
import Combobox from "@/components/ui/combobox"
import { useEffect, useState } from "react"

// Leakage type options - should come from API in production
const leakageTypeOptions: SelectOption[] = [
  { value: "Horizontal Seal Open", label: "Horizontal Seal Open" },
  { value: "Horizontal Seal Wrinkle", label: "Horizontal Seal Wrinkle" },
  { value: "Jaw Cut", label: "Jaw Cut" },
  { value: "Micro Leakage", label: "Micro Leakage" },
  { value: "Pinhole", label: "Pinhole" },
  { value: "Product in Seal", label: "Product in Seal" },
  { value: "Vertical Seal Open", label: "Vertical Seal Open" },
  { value: "Vertical Seal Wrinkle", label: "Vertical Seal Wrinkle" },
]

function parseLeakageValue(value: string | undefined): { entries: Array<{id: string; count: number; type: string}>; remark?: string } {
  if (!value) {
    return { entries: [] }
  }
  try {
    return JSON.parse(value)
  } catch {
    return { entries: [] }
  }
}

function isPrimaryPackagingCheckedParam(param: V2ParamFormData) {
  return param.name.toLowerCase().includes("check") && !param.name.toLowerCase().includes("leakage")
}

function isPrimaryPackagingLeakedParam(param: V2ParamFormData) {
  return param.name.toLowerCase().includes("leaked")
}

function isPrimaryPackagingBreakdownParam(param: V2ParamFormData) {
  const paramName = param.name.toLowerCase()
  return paramName.includes("breakdown") || paramName.includes("leakage type")
}

export interface SectionFormProps {
  className?: string
  section: V2SectionFormData
  disabled?: boolean
  onParamChange?: (paramId: string, value: string) => void
  onWindowChange?: (paramId: string, windowIndex: number, value: string) => void
  onToggleExpand?: () => void
  onClickErrorFlag?: (paramId: string, windowIndex?: number) => void
}

/**
 * Collapsible section form for rendering parameters
 */
export default function SectionForm({
  className,
  section,
  disabled = false,
  onParamChange,
  onWindowChange,
  onToggleExpand,
  onClickErrorFlag,
}: SectionFormProps) {
  const isComplete = isSectionComplete(section)
  const isSubmitted = section.status === FormParamStatus.SUBMITTED
  const isPrimaryPackaging = section.type === V2ParameterSection.PRIMARY_PACKAGING
  const checkedParam = isPrimaryPackaging
    ? section.parameters.find(isPrimaryPackagingCheckedParam)
    : undefined
  const leakedParam = isPrimaryPackaging
    ? section.parameters.find(isPrimaryPackagingLeakedParam)
    : undefined
  const breakdownParam = isPrimaryPackaging
    ? section.parameters.find(isPrimaryPackagingBreakdownParam)
    : undefined
  const totalLeaked = Number(leakedParam?.value) || 0
  const showLeakageWidget = Boolean(breakdownParam && totalLeaked > 0)

  useEffect(() => {
    if (!isPrimaryPackaging || isSubmitted || !breakdownParam?.id) {
      return
    }

    if (totalLeaked > 0 || !breakdownParam.value) {
      return
    }

    onParamChange?.(breakdownParam.id, "")
  }, [
    breakdownParam?.id,
    breakdownParam?.value,
    isPrimaryPackaging,
    isSubmitted,
    onParamChange,
    totalLeaked,
  ])

  function handleToggleExpand() {
    if (onToggleExpand) {
      onToggleExpand()
    }
  }

  // Skip Information Fields section (handled by ItemHeader)
  if (section.type === V2ParameterSection.INFORMATION_FIELDS) {
    return null
  }

  return (
    <div
      className={cn(
        "border rounded-lg bg-white overflow-hidden",
        className
      )}
    >
      {/* Section Header - Different for readonly vs edit mode */}
      {isSubmitted ? (
        // Readonly header - matches old packaging style
        <h3 className="flex justify-between items-center py-2.5 px-3 bg-band-fill uppercase text-sm font-semibold">
          {section.name}
        </h3>
      ) : (
        // Edit mode - collapsible header
        <button
          type="button"
          className={cn(
            "w-full flex items-center justify-between p-4 text-left",
            "hover:bg-gray-50 transition-colors",
            section.isExpanded && "border-b"
          )}
          onClick={handleToggleExpand}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-button-black">{section.name}</span>
            {isComplete && (
              <Icon name="Check" className="size-5 text-green-500" />
            )}
            {!isComplete && section.parameters.length > 0 && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                {`${section.parameters.filter((p) => p.value || p.windows?.some((w) => w.value)).length}/${section.parameters.length}`}
              </span>
            )}
          </div>
          <Icon
            name="ChevronUp"
            className={cn(
              "size-5 text-gray-500 transition-transform",
              !section.isExpanded && "rotate-180"
            )}
          />
        </button>
      )}

      {/* Section Content - Always show for submitted, collapsible for edit */}
      {(section.isExpanded || isSubmitted) && (
        <div className={cn(
          isSubmitted
            ? "grid grid-cols-2 gap-x-3 gap-y-4 px-3 pt-3 pb-4"
            : "p-4 space-y-4"
        )}>
          {section.parameters.length === 0 ? (
            <p className="text-gray-500 text-sm col-span-2">No parameters for this section</p>
          ) : isSubmitted ? (
            // Readonly mode - simple grid layout matching old packaging
            <>
              {showLeakageWidget && breakdownParam && (
                <div className="col-span-2">
                  <LeakageWidget
                    value={parseLeakageValue(breakdownParam.value)}
                    paramRemark={breakdownParam.remark}
                    onChange={() => {}}
                    leakageTypes={leakageTypeOptions}
                    totalChecked={Number(checkedParam?.value) || 0}
                    totalLeaked={totalLeaked}
                    disabled={true}
                    isSubmitted={true}
                    onClickErrorFlag={() => onClickErrorFlag?.(breakdownParam.id)}
                  />
                </div>
              )}
              {section.parameters
                .filter((param) => param.id !== breakdownParam?.id)
                .map((param) => (
                  <ParameterReadOnly
                    key={param.id}
                    param={param}
                    onClickErrorFlag={() => onClickErrorFlag?.(param.id)}
                  />
                ))}
            </>
          ) : (
            // Edit mode - existing complex layouts
            <>
              {(() => {
                // For Machine Check: frequency params first (full width), then normal params in two column layout
                if (section.type === V2ParameterSection.MACHINE_CHECK) {
                  // First separate frequency parameters (they need full width)
                  const frequencyParams = section.parameters.filter(
                    (p) => p.frequencyMinutes && p.windows
                  )
                  const normalParams = section.parameters.filter(
                    (p) => !p.frequencyMinutes || !p.windows
                  )

                  return (
                    <>
                      {/* Frequency parameters - full width (not in grid) */}
                      {frequencyParams.map((param) => (
                        <ParameterInput
                          key={param.id}
                          param={param}
                          disabled={disabled}
                          onValueChange={(value) => onParamChange?.(param.id, value)}
                          onWindowChange={(windowIndex, value) =>
                            onWindowChange?.(param.id, windowIndex, value)
                          }
                          onClickErrorFlag={(windowIndex) =>
                            onClickErrorFlag?.(param.id, windowIndex)
                          }
                        />
                      ))}

                      {/* Normal parameters - two column layout */}
                      {normalParams.length > 0 && (
                        <div className="grid grid-cols-2 gap-4">
                          {normalParams.map((param) => (
                            <ParameterInput
                              key={param.id}
                              param={param}
                              disabled={disabled}
                              onValueChange={(value) => onParamChange?.(param.id, value)}
                              onWindowChange={(windowIndex, value) =>
                                onWindowChange?.(param.id, windowIndex, value)
                              }
                              onClickErrorFlag={(windowIndex) =>
                                onClickErrorFlag?.(param.id, windowIndex)
                              }
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )
                }

                // For Labelling Compliance: frequency params first (full width), then normal params in two column layout
                if (section.type === V2ParameterSection.LABELLING_COMPLIANCE) {
                  // First separate frequency parameters (they need full width)
                  const frequencyParams = section.parameters.filter(
                    (p) => p.frequencyMinutes && p.windows
                  )
                  const normalParams = section.parameters.filter(
                    (p) => !p.frequencyMinutes || !p.windows
                  )

                  return (
                    <>
                      {/* Frequency parameters - full width (not in grid) */}
                      {frequencyParams.map((param) => (
                        <ParameterInput
                          key={param.id}
                          param={param}
                          disabled={disabled}
                          onValueChange={(value) => onParamChange?.(param.id, value)}
                          onWindowChange={(windowIndex, value) =>
                            onWindowChange?.(param.id, windowIndex, value)
                          }
                          onClickErrorFlag={(windowIndex) =>
                            onClickErrorFlag?.(param.id, windowIndex)
                          }
                        />
                      ))}

                      {/* Normal parameters - two column layout */}
                      {normalParams.length > 0 && (
                        <div className="grid grid-cols-2 gap-4">
                          {normalParams.map((param) => (
                            <ParameterInput
                              key={param.id}
                              param={param}
                              disabled={disabled}
                              onValueChange={(value) => onParamChange?.(param.id, value)}
                              onWindowChange={(windowIndex, value) =>
                                onWindowChange?.(param.id, windowIndex, value)
                              }
                              onClickErrorFlag={(windowIndex) =>
                                onClickErrorFlag?.(param.id, windowIndex)
                              }
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )
                }

                // For Primary Packaging: frequency params first (full width), then input fields, then other fields
                if (section.type === V2ParameterSection.PRIMARY_PACKAGING) {
                  // First separate frequency parameters (they need full width)
                  const frequencyParams = section.parameters.filter(
                    (p) => p.frequencyMinutes && p.windows
                  )
                  const nonFrequencyParams = section.parameters.filter(
                    (p) => !p.frequencyMinutes || !p.windows
                  )

                  const regularNonFreqParams = nonFrequencyParams.filter(
                    (param) => param.id !== breakdownParam?.id
                  )

                  // Separate remaining fields by input type
                  const inputFields = regularNonFreqParams.filter(
                    (p) => p.inputType === V2InputType.VALUE_NUMBER || p.inputType === V2InputType.VALUE_TEXT
                  )
                  const otherFields = regularNonFreqParams.filter(
                    (p) => p.inputType !== V2InputType.VALUE_NUMBER && p.inputType !== V2InputType.VALUE_TEXT
                  )

                  // Split input fields: first 2 side-by-side, rest full-width horizontal
                  const firstTwoInputs = inputFields.slice(0, 2)
                  const remainingInputs = inputFields.slice(2)

                  return (
                    <>
                      {/* Frequency parameters - full width (not in grid) */}
                      {frequencyParams.map((param) => (
                        <ParameterInput
                          key={param.id}
                          param={param}
                          disabled={disabled}
                          onValueChange={(value) => onParamChange?.(param.id, value)}
                          onWindowChange={(windowIndex, value) =>
                            onWindowChange?.(param.id, windowIndex, value)
                          }
                          onClickErrorFlag={(windowIndex) =>
                            onClickErrorFlag?.(param.id, windowIndex)
                          }
                        />
                      ))}

                      {/* First two input fields - side by side, label on top */}
                      {firstTwoInputs.length > 0 && (
                        <div className="grid grid-cols-2 gap-4">
                          {firstTwoInputs.map((param) => (
                            <ParameterInput
                              key={param.id}
                              param={param}
                              disabled={disabled}
                              onValueChange={(value) => onParamChange?.(param.id, value)}
                              onWindowChange={(windowIndex, value) =>
                                onWindowChange?.(param.id, windowIndex, value)
                              }
                              onClickErrorFlag={(windowIndex) =>
                                onClickErrorFlag?.(param.id, windowIndex)
                              }
                            />
                          ))}
                        </div>
                      )}

                      {/* Leakage Widget - shown when total leaked > 0 */}
                      {showLeakageWidget && breakdownParam && (
                        <div>
                          <LeakageWidget
                            value={parseLeakageValue(breakdownParam.value)}
                            paramRemark={breakdownParam.remark}
                            onChange={(data) => {
                              onParamChange?.(breakdownParam.id, JSON.stringify(data))
                            }}
                            leakageTypes={leakageTypeOptions}
                            totalChecked={Number(checkedParam?.value) || 0}
                            totalLeaked={Number(leakedParam?.value) || 0}
                            disabled={disabled}
                            isSubmitted={isSubmitted}
                            onClickErrorFlag={() => onClickErrorFlag?.(breakdownParam.id)}
                          />
                        </div>
                      )}

                      {/* Remaining input fields - full width, label on left */}
                      {remainingInputs.map((param) => (
                        <ParameterInput
                          key={param.id}
                          param={param}
                          layout="horizontal"
                          disabled={disabled}
                          onValueChange={(value) => onParamChange?.(param.id, value)}
                          onWindowChange={(windowIndex, value) =>
                            onWindowChange?.(param.id, windowIndex, value)
                          }
                          onClickErrorFlag={(windowIndex) =>
                            onClickErrorFlag?.(param.id, windowIndex)
                          }
                        />
                      ))}

                      {/* Other fields (CHOICE, DROPDOWN, etc.) - two column layout */}
                      {otherFields.length > 0 && (
                        <div className="grid grid-cols-2 gap-4">
                          {otherFields.map((param) => (
                            <ParameterInput
                              key={param.id}
                              param={param}
                              disabled={disabled}
                              onValueChange={(value) => onParamChange?.(param.id, value)}
                              onWindowChange={(windowIndex, value) =>
                                onWindowChange?.(param.id, windowIndex, value)
                              }
                              onClickErrorFlag={(windowIndex) =>
                                onClickErrorFlag?.(param.id, windowIndex)
                              }
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )
                }

                // Default: single column layout (for Secondary Packaging and others)
                // First separate frequency parameters (they need full width)
                const frequencyParams = section.parameters.filter(
                  (p) => p.frequencyMinutes && p.windows
                )
                const normalParams = section.parameters.filter(
                  (p) => !p.frequencyMinutes || !p.windows
                )

                return (
                  <>
                    {/* Frequency parameters - full width (not in grid) */}
                    {frequencyParams.map((param) => (
                      <ParameterInput
                        key={param.id}
                        param={param}
                        disabled={disabled}
                        onValueChange={(value) => onParamChange?.(param.id, value)}
                        onWindowChange={(windowIndex, value) =>
                          onWindowChange?.(param.id, windowIndex, value)
                        }
                        onClickErrorFlag={(windowIndex) =>
                          onClickErrorFlag?.(param.id, windowIndex)
                        }
                      />
                    ))}

                    {/* Normal parameters - single column */}
                    {normalParams.map((param) => (
                      <ParameterInput
                        key={param.id}
                        param={param}
                        disabled={disabled}
                        onValueChange={(value) => onParamChange?.(param.id, value)}
                        onWindowChange={(windowIndex, value) =>
                          onWindowChange?.(param.id, windowIndex, value)
                        }
                        onClickErrorFlag={(windowIndex) =>
                          onClickErrorFlag?.(param.id, windowIndex)
                        }
                      />
                    ))}
                  </>
                )
              })()}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Individual parameter input based on type
 */
interface ParameterInputProps {
  param: V2ParamFormData
  disabled?: boolean
  layout?: "vertical" | "horizontal"
  onValueChange?: (value: string) => void
  onWindowChange?: (windowIndex: number, value: string) => void
  onClickErrorFlag?: (windowIndex?: number) => void
}

function ParameterInput({
  param,
  disabled = false,
  layout = "vertical",
  onValueChange,
  onWindowChange,
  onClickErrorFlag,
}: ParameterInputProps) {
  const [localValue, setLocalValue] = useState(param.value || "")
  const isSubmitted = param.status === FormParamStatus.SUBMITTED

  // Handle frequency-based parameters
  if (param.frequencyMinutes && param.windows) {
    return (
      <FrequencyWidget
        param={param}
        disabled={disabled}
        onWindowChange={onWindowChange}
        onClickErrorFlag={onClickErrorFlag}
      />
    )
  }

  function handleChange(value: string) {
    setLocalValue(value)
    onValueChange?.(value)
  }

  // Render based on input type
  switch (param.inputType) {
    case V2InputType.CHOICE:
      return (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
          <div>
            {isSubmitted ? (
              <span className="font-medium text-sm">{param.value || "-"}</span>
            ) : (
              <ToggleGroup
                options={
                  param.possibleValues?.map((v) => ({ label: v, value: v })) || [
                    { label: "OK", value: "OK" },
                    { label: "NOT OK", value: "NOT OK" },
                  ]
                }
                value={localValue}
                correctValue={param.correctValue}
                disabled={disabled}
                flagState={getParamErrorFlagState(param)}
                onChange={handleChange}
                onClickRedFlag={() => onClickErrorFlag?.()}
              />
            )}
          </div>
        </div>
      )

    case V2InputType.VALUE_NUMBER: {
      const isOutOfRange = localValue &&
        isValueWithinLimits(localValue, param.minLimit, param.maxLimit) !== "valid"
      const labelText = `${param.name}${param.unit ? ` (${param.unit})` : ""}`
      return (
        <div className={cn(
          layout === "horizontal"
            ? "flex items-start gap-4"
            : "flex flex-col gap-2"
        )}>
          <Label
            className={cn(
              "text-sm font-medium",
              layout === "horizontal" ? "w-1/2 pt-2" : "truncate"
            )}
            title={labelText}
          >
            {param.name}
            {param.unit && <span className="text-gray-500">({param.unit})</span>}
          </Label>
          <div className={cn(layout === "horizontal" && "w-1/2")}>
            {isSubmitted ? (
              <span className="font-medium text-sm">
                {param.value || "-"}
                {param.unit && ` ${param.unit}`}
              </span>
            ) : (
              <div className="relative">
                <Input
                  type="number"
                  value={localValue}
                  disabled={disabled}
                  onChange={(e) => handleChange(e.target.value)}
                  className={cn(
                    isOutOfRange && "border-red-500 pr-10"
                  )}
                />
                {isOutOfRange && (
                  <button
                    type="button"
                    className="absolute top-3 right-3 hover:cursor-pointer"
                    onClick={() => onClickErrorFlag?.()}
                  >
                    <Icon
                      name={param.remark ? "RedFlag" : "FlagNeutral"}
                      className="h-4 w-4"
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }

    case V2InputType.VALUE_TEXT:
      return (
        <div className={cn(
          layout === "horizontal"
            ? "flex items-start gap-4"
            : "flex flex-col gap-2"
        )}>
          <Label
            className={cn(
              "text-sm font-medium",
              layout === "horizontal" ? "w-1/2 pt-2" : "truncate"
            )}
            title={param.name}
          >
            {param.name}
          </Label>
          <div className={cn(layout === "horizontal" && "w-1/2")}>
            {isSubmitted ? (
              <span className="font-medium text-sm">{param.value || "-"}</span>
            ) : (
              <Input
                type="text"
                value={localValue}
                disabled={disabled}
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          </div>
        </div>
      )

    case V2InputType.DROPDOWN:
    case V2InputType.RADIO:
      const dropdownOptions = param.possibleValues?.map((v) => ({ label: v, value: v })) || []
      const isVendor = param.name === "Vendor Name" || param.name.toLowerCase().includes("vendor")
      
      // Use Combobox for Vendor (searchable) - same as old packaging module
      if (isVendor && !isSubmitted) {
        return (
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
            <div>
              <Combobox
                placeholder={`Select ${param.name}`}
                options={dropdownOptions}
                defaultValue={localValue ? [localValue] : []}
                multiple={false}
                disabled={disabled}
                onChange={(selected) => {
                  const selectedValue = selected[0]
                  if (selectedValue) {
                    handleChange(selectedValue)
                  }
                }}
              />
            </div>
          </div>
        )
      }
      
      return (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
          <div>
            {isSubmitted ? (
              <span className="font-medium text-sm">{param.value || "-"}</span>
            ) : (
              <Select
                placeholder={`Select ${param.name}`}
                options={dropdownOptions}
                value={localValue}
                disabled={disabled}
                onChange={(value) => handleChange(value)}
              />
            )}
          </div>
        </div>
      )

    case V2InputType.DATE:
      return (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
          <div>
            {isSubmitted ? (
              <span className="font-medium text-sm">{param.value || "-"}</span>
            ) : (
              <DatePicker
                value={localValue}
                onChange={(date) => handleChange(date)}
              />
            )}
          </div>
        </div>
      )

    default:
      return (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>
          <div>
            <Input
              type="text"
              value={localValue}
              disabled={disabled}
              onChange={(e) => handleChange(e.target.value)}
            />
          </div>
        </div>
      )
  }
}

/**
 * Readonly parameter display matching old packaging module style
 * All parameters use simple vertical layout: Label on top, Value below
 * Fits in 2-column grid (col-span-1 by default)
 */
function ParameterReadOnly({
  param,
  onClickErrorFlag,
}: {
  param: V2ParamFormData
  onClickErrorFlag?: () => void
}) {
  // For frequency/window parameters - show each window value
  if (param.windows && param.windows.length > 0) {
    return (
      <div className="flex flex-col gap-1">
        <Label className="text-sm font-medium">
          {param.name}
          {param.unit && <span className="text-gray-500">({param.unit})</span>}
        </Label>
        <div className="flex flex-col gap-1">
          {param.windows.map((window) => (
            <div key={window.id} className="flex items-center gap-2 text-sm">
              <span className="text-label-text text-xs">
                {formatTimeWindowForDisplay(window.startTime, window.endTime)}:
              </span>
              <span className="font-medium">{window.value || "-"}</span>
              {window.remark && (
                <button type="button" className="hover:cursor-pointer" onClick={onClickErrorFlag}>
                  <Icon name="RedFlag" className="size-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // For all types - simple vertical layout: Label on top, Value below
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-sm font-medium">
        {param.name}
        {param.unit && <span className="text-gray-500">({param.unit})</span>}
      </Label>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium">{param.value || "-"}</p>
        {param.remark && (
          <button type="button" className="hover:cursor-pointer" onClick={onClickErrorFlag}>
            <Icon name="RedFlag" className="size-4" />
          </button>
        )}
      </div>
    </div>
  )
}
